// Mirrors CANCELLATION_POLICIES in backend/server.py — keep in sync
export const CANCELLATION_POLICIES = {
  flexible: { label: 'Flexible', desc: 'Full refund up to 48 hours before the event.' },
  moderate: { label: 'Moderate', desc: 'Full refund up to 7 days before the event; 50% refund until 48 hours before.' },
  strict: { label: 'Strict', desc: 'Deposit is non-refundable. Balance refundable up to 7 days before the event.' },
  custom: { label: 'Custom', desc: 'Vendor-set cancellation fee within a chosen window before the event.' },
};

// Human-readable fee, e.g. "$50" or "20%"
export function feeLabel(type, amount) {
  const n = Number(amount) || 0;
  return type === 'flat' ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : `${n}%`;
}

// Describe a provider/booking's cancellation policy in one sentence.
// Accepts an object carrying cancellation_policy + the custom fee fields.
export function describePolicy(p) {
  if (!p) return CANCELLATION_POLICIES.flexible.desc;
  if (p.cancellation_policy === 'custom' && p.cancel_fee_window_days != null) {
    const d = p.cancel_fee_window_days;
    const win = `${d} day${d === 1 ? '' : 's'}`;
    return `Free cancellation up to ${win} before the event. Cancel within ${win} of the event and a ${feeLabel(p.cancel_fee_type, p.cancel_fee_amount)} cancellation fee applies.`;
  }
  return (CANCELLATION_POLICIES[p.cancellation_policy] || CANCELLATION_POLICIES.flexible).desc;
}

// Days between now and an event date string (YYYY-MM-DD), floored to whole days.
function daysUntil(dateStr) {
  if (!dateStr) return 9999;
  const event = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((event - today) / 86400000);
}

// How much a customer has actually been charged on a booking so far.
// pending = created but unpaid; confirmed = deposit or full paid; split plans
// may have only the deposit paid until the balance is collected.
function amountPaid(b) {
  const paidStatus = b?.status === 'confirmed' || b?.status === 'completed';
  if ((Number(b?.balance_amount) || 0) > 0) {
    let p = 0;
    if (paidStatus) p += Number(b?.deposit_amount) || 0;
    if (b?.balance_paid) p += Number(b?.balance_amount) || 0;
    return p;
  }
  return paidStatus ? (Number(b?.total_paid) || 0) : 0;
}

// Preview the fee/refund a customer would face if they cancel this booking NOW.
// Mirrors the backend refund logic (best-effort; the server is the source of truth).
// Returns { fee, refund, paid, free } in dollars.
export function computeCancellation(booking) {
  const paid = amountPaid(booking);
  const total = Number(booking?.total_paid) || 0; // full booking value the fee is defined against
  const policy = booking?.cancellation_policy || 'flexible';
  const d = daysUntil(booking?.booking_date);
  let frac = 1; // refundable fraction of what was paid
  if (policy === 'custom') {
    const win = booking?.cancel_fee_window_days;
    if (win && d < win && total > 0) {
      const amt = Number(booking?.cancel_fee_amount) || 0;
      const fee = booking?.cancel_fee_type === 'flat' ? Math.min(amt, total) : (total * Math.max(0, Math.min(amt, 100))) / 100;
      frac = Math.max(0, (total - fee) / total);
    }
  } else if (policy === 'strict') {
    frac = d >= 7 ? 0.5 : 0; // deposit non-refundable; ~half back on a full payment 7+ days out
  } else if (policy === 'moderate') {
    frac = d >= 7 ? 1 : d >= 2 ? 0.5 : 0;
  } else {
    frac = d >= 2 ? 1 : 0; // flexible
  }
  const refund = Math.round(paid * frac * 100) / 100;
  return { paid, refund, fee: Math.round((paid - refund) * 100) / 100, free: paid === 0 || refund >= paid };
}

export const DEPOSIT_OPTIONS = [
  { value: 0, label: 'Full payment upfront' },
  { value: 25, label: '25% deposit — balance due 7 days before the event' },
  { value: 50, label: '50% deposit — balance due 7 days before the event' },
];

// Turn a YouTube/Vimeo link into an embeddable URL (null if unrecognized)
export function embedUrl(link) {
  if (!link) return null;
  const yt = link.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = link.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}
